import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import EllipsisHorizontal from 'apollo-react-icons/EllipsisHorizontal';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MenuItemContentWithIcon from '../../../components/MenuItemContentWithIcon';
import { getUITA } from '../../../utils/commonUtils';
import {
  updateControlProperties,
  updateControlProperty,
} from '../../actions/report-designer.actions';
import {
  CONDITIONAL_SELECT,
  CONDITIONAL_TEXT_FIELD,
  PROPERTIES_WITH_ENTITY_FIELDS_SELECT,
  PROPERTIES_WITH_ROOT_ENTITY_SELECT,
  RADIO,
  SELECT,
} from '../../constants/report-designer.constants';
import {
  getControlProperty,
  getEntityFieldsBySourceFieldName,
  getHasValidationErrorByIdAndProp,
  getIsControlPropChanged,
  getIsNewAddedControl,
  getReportModelEntitiesList,
} from '../../selectors/report-designer.selectors';
import { getPropertyValueToDisplay, isExpressionValue } from '../../utils/report-designer.utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    '& div[class*="MuiFormGroup-root"]': {
      padding: '7px',
    },
    '& svg': {
      fontSize: '16px',
    },
    '& .ui-changes div[class*="MuiSelect-select"], .ui-changes input[class*="MuiInputBase-input"]': {
      borderColor: 'orange',
    },
    '& div[class*="MuiFormGroup-root"].ui-changes': {
      border: '1px solid orange',
    },
  },
  iconButton: {
    border: '1px solid #d9d9d9',
    marginTop: '42px',
    borderRadius: '0px 4px 4px 0px',
    borderLeft: 'none',
    width: '35px',
    height: '2.5rem',
    '& svg': {
      margin: '10px 8px',
    },
    '&:disabled': {
      borderColor: '#b6b5b5',
    },
    cursor: 'pointer',
  },
  radioGroupWrapper: {
    marginTop: 16,
    '& div[role=radiogroup]': {
      padding: '0 7px',
    },
  },
});

const PropertyComponent = ({
  type,
  controlId,
  values,
  name,
  label,
  onOpen,
  entitySourceField,
  dependentProperties = [],
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [value, setValue] = useState();

  const propValueStore = useSelector((state) =>
    getControlProperty(state, { id: controlId, prop: name })
  );
  const isControlPropChanged = useSelector((state) =>
    getIsControlPropChanged(state, { id: controlId, prop: name })
  );
  const isNewAddedControl = useSelector((state) => getIsNewAddedControl(state, { id: controlId }));
  const reportModelEntitiesList = useSelector(getReportModelEntitiesList);
  const entityFieldsList = useSelector((state) =>
    getEntityFieldsBySourceFieldName(state, { id: controlId, sourceField: entitySourceField })
  );
  const hasValidationError = useSelector((state) =>
    getHasValidationErrorByIdAndProp(state, { id: controlId, prop: name })
  );

  useEffect(() => {
    const valueToDisplay = getPropertyValueToDisplay(propValueStore);

    setValue(valueToDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValueStore]);

  const getSelectValues = () => {
    if (PROPERTIES_WITH_ROOT_ENTITY_SELECT.includes(name)) {
      return reportModelEntitiesList;
    }

    return values || [];
  };

  const getConditionalSelectValues = () => {
    if (PROPERTIES_WITH_ENTITY_FIELDS_SELECT.includes(name)) {
      return [{ label: 'Conditional...', value: 'conditional' }, ...entityFieldsList];
    }

    return values || [];
  };

  const updateProperty = ({ target: { value } }) => {
    let parsedValueTemp = value;

    if (value === 'true' || value === true) {
      parsedValueTemp = true;
    } else if (value === 'false' || value === false) {
      parsedValueTemp = false;
    } else if (value === 'conditional') {
      onOpen();
      return;
    }

    if (parsedValueTemp !== propValueStore) {
      if (dependentProperties.length) {
        const ownProp = { propName: name, propValue: parsedValueTemp };

        const dependentPropsToUpdate = dependentProperties.map((propertyName) => ({
          propName: propertyName,
          propValue: '',
        }));

        dispatch(updateControlProperties(controlId, [ownProp, ...dependentPropsToUpdate]));
        return;
      }

      dispatch(updateControlProperty(controlId, name, parsedValueTemp));
    }
  };

  const isConditionalSelect =
    (type === CONDITIONAL_SELECT && value === 'conditional') || isExpressionValue(propValueStore);

  const hasIconInOptions = ['palette', 'color', 'backGroundColor'].includes(name);

  switch (type) {
    case CONDITIONAL_SELECT:
      return (
        <div className={classes.root}>
          <Select
            fullWidth
            canDeselect={false}
            className={{ 'ui-changes': !isNewAddedControl && isControlPropChanged }}
            label={label}
            value={isExpressionValue(propValueStore) ? 'conditional' : value}
            onChange={updateProperty}
            ui-ta={getUITA('CtrlProp', name)}
            error={hasValidationError}
          >
            {getConditionalSelectValues().map(({ value, label, icon }) => (
              <MenuItem key={value} value={value}>
                {hasIconInOptions ? (
                  <MenuItemContentWithIcon label={label} icon={icon} noPadding />
                ) : (
                  label
                )}
              </MenuItem>
            ))}
          </Select>
          {isConditionalSelect && (
            <IconButton
              className={classes.iconButton}
              onClick={onOpen}
              ui-ta={getUITA('Edit', name, 'condition')}
            >
              <EllipsisHorizontal />
            </IconButton>
          )}
        </div>
      );

    case SELECT:
      return (
        <div className={classes.root}>
          <Select
            fullWidth
            canDeselect={false}
            className={{ 'ui-changes': !isNewAddedControl && isControlPropChanged }}
            label={label}
            value={value ?? ''}
            onChange={updateProperty}
            ui-ta={getUITA('CtrlProp', name)}
            error={hasValidationError}
          >
            {getSelectValues().map(({ value, label, icon }) => (
              <MenuItem key={value} value={value}>
                {hasIconInOptions ? (
                  <MenuItemContentWithIcon label={label} icon={icon} noPadding />
                ) : (
                  label
                )}
              </MenuItem>
            ))}
          </Select>
        </div>
      );

    case CONDITIONAL_TEXT_FIELD:
      return (
        <div className={classes.root}>
          <TextField
            fullWidth
            className={{ 'ui-changes': !isNewAddedControl && isControlPropChanged }}
            label={label}
            value={value ?? ''}
            icon={<EllipsisHorizontal />}
            iconProps={{ onClick: onOpen }}
            onBlur={updateProperty}
            onChange={({ target: { value } }) => setValue(value)}
            ui-ta={getUITA('CtrlProp', name)}
            error={hasValidationError}
          />
        </div>
      );

    case RADIO:
      return (
        <div className={classes.root}>
          <div className={classes.radioGroupWrapper}>
            <RadioGroup
              row
              className={{ 'ui-changes': !isNewAddedControl && isControlPropChanged }}
              label={label}
              aria-label="label"
              name="label"
              value={value ?? ''}
              onChange={updateProperty}
              ui-ta={getUITA('CtrlProp', name)}
              error={hasValidationError}
            >
              {values.map(({ value, label }) => (
                <Radio key={value} value={value} label={label} />
              ))}
            </RadioGroup>
          </div>
        </div>
      );
  }
};

export default PropertyComponent;
